import { Room, RoomEvent, Track } from 'livekit-client';

// Connect as a listen-only subscriber and render the host's video + audio into a
// single <video>. Returns the connected Room — call room.disconnect() to leave.
//   getVideoEl: () => HTMLVideoElement | null   (a getter, so we always attach to
//               the live element and never a stale/null ref)
//   onStatus:   (state) => void   states: 'connecting' | 'waiting' | 'live' |
//               'error' | 'disconnected'   (drives the on-screen status)
//
// Token comes from the origin-checked server endpoint, so the LiveKit secret
// never reaches the browser.
export async function connectLiveViewer({ serverUrl, roomCode, getVideoEl, onStatus }) {
  const log = (...a) => { try { console.log('[live]', ...a); } catch {} };
  onStatus?.('connecting');
  log('requesting token for', roomCode);

  const res = await fetch(`${serverUrl}/live-token-web`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomCode }),
  });
  if (!res.ok) { onStatus?.('error'); throw new Error('live token failed: ' + res.status); }
  const { url, token } = await res.json();
  log('got token; connecting to', url);

  // NO adaptiveStream: we render into one fixed <video>. adaptiveStream can keep
  // the remote video paused/black when it can't reliably measure the element.
  const room = new Room();

  const attach = (track, participant) => {
    const el = getVideoEl?.();
    log('attach', track.kind, 'from', participant?.identity, '· element present:', !!el);
    if (!el) return;
    try { track.attach(el); } catch (e) { log('attach error', e?.message); }
    if (track.kind === Track.Kind.Video) {
      onStatus?.('live');
      // Muted autoplay is always allowed; the viewer unmutes with a tap. This
      // avoids the autoplay-with-audio block that otherwise leaves it black.
      el.muted = true;
      const p = el.play?.();
      if (p && p.catch) p.catch((e) => log('play() blocked:', e?.message));
    }
  };

  room
    .on(RoomEvent.TrackSubscribed, (track, _pub, participant) => attach(track, participant))
    .on(RoomEvent.ParticipantConnected, (p) => { log('participant connected:', p.identity); onStatus?.('waiting'); })
    .on(RoomEvent.Disconnected, () => { log('disconnected'); onStatus?.('disconnected'); });

  await room.connect(url, token);
  log('room connected · remote participants:', room.remoteParticipants.size);
  onStatus?.('waiting');

  // Attach any tracks already published before we joined.
  room.remoteParticipants.forEach((p) => {
    p.trackPublications.forEach((pub) => {
      log('existing publication:', pub.kind, '· subscribed:', pub.isSubscribed, '· hasTrack:', !!pub.track);
      if (pub.track) attach(pub.track, p);
    });
  });

  return room;
}
